import {
  connectOmniChannelSocket,
  offReceiveGetCallDetailsEvent,
  onReceiveAgentUpdateNotification,
  onReceiveGetCallDetailsEvent,
  onReceiveTotalCallandChat
} from 'services';
import { store } from '../../store';
import { clearSeatData, incrementCounter, updateSeatComprehensive } from 'store/features/agent-map/agent-map.slice';

// New DTO interface for dashboard updates
interface DashboardUpdateDto {
  callSource?: string;
  updates?: {
    field: string;
    action: string;
    value?: number;
  }[];
}

// Global event buffer to deduplicate events at module level
const recentEvents = new Map<string, { timestamp: number; hash: string }>();
const GLOBAL_DEDUP_WINDOW = 3000; // 3 seconds

// Create a stable hash for an object
function objectHash(obj: any): string {
  if (!obj) return 'null';
  const str = JSON.stringify(obj, Object.keys(obj).sort());
  return str;
}

/**
 * Singleton để quản lý socket connections toàn cục
 */
class SocketService {
  private static instance: SocketService;
  private isInitialized = false;
  private lastProcessedCounters: Record<string, number> = {};
  private lastProcessedStatus: Record<string, { data: any; timestamp: number }> = {};

  // Enhance the dashboard update tracking with more detail
  private lastProcessedDashboardUpdates: Record<
    string,
    {
      timestamp: number;
      state: string; // Store the full state to avoid processing identical updates
      processCount: number; // Track how many times we've seen this update
    }
  > = {};

  // Add a deduplication timeout setting (milliseconds)
  private readonly DASHBOARD_EVENT_DEDUP_TIMEOUT = 5000; // 5 seconds

  // Add flag to ensure we bind dashboard handler only once
  private dashboardHandlerInitialized = false;

  private constructor() {}

  public static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  public initialize(): void {
    if (this.isInitialized) return;

    // console.log('🔌 SocketService: Khởi tạo socket connections');
    connectOmniChannelSocket();

    // Set up status handler
    this.setupStatusHandler();

    // Set up counter handler
    this.setupCounterHandler();

    // Set up dashboard updates handler
    this.setupDashboardUpdatesHandler();

    this.isInitialized = true;
  }

  public reinitialize(): void {
    // console.log('🔄 SocketService: Reconnecting socket only, NOT rebinding handlers');

    // Only reconnect, don't rebind handlers
    connectOmniChannelSocket();

    // Don't reset initialized flags - keep them as-is
    // this.isInitialized = false;
    // this.dashboardHandlerInitialized = false;

    // Don't call this.initialize();
  }

  private setupStatusHandler(): void {
    // console.log('khởi tạo socket status handler');
    onReceiveAgentUpdateNotification((data) => {
      // Detailed logging of the raw socket data
      // console.log('🔔 SOCKET RAW STATUS DATA RECEIVED:', JSON.stringify(data, null, 2));
      // console.log('🔑 Socket data keys:', data ? Object.keys(data) : 'NULL');

      if (!data || !data.agentMapSeatId) return;

      // Get existing state to check for the seat
      const state = store.getState();
      const onlineData = state.agentMap.onlineDataForChart || [];
      const existingSeat = onlineData.find((s) => s.agentMapSeatId === data.agentMapSeatId);

      // If we have the seat, preserve its important properties (extensionId and shortName)
      // when constructing the update payload
      let statusUpdatePayload: any = {
        agentMapSeatId: data.agentMapSeatId,
        statusName: data.statusName || data.nextState,
        agentChangeStatusDate: data.agentChangeStatusDate || new Date().toISOString()
      };

      // CRITICAL: Preserve extensionId and shortName when they're missing in the update
      if (existingSeat) {
        // Only use original values if the update doesn't provide them
        if (!data.extensionId && existingSeat.extensionId) {
          statusUpdatePayload.extensionId = existingSeat.extensionId;
          // console.log(
          //   `⚠️ Preserving existing extensionId: ${existingSeat.extensionId} for seat ${data.agentMapSeatId}`
          // );
        } else {
          statusUpdatePayload.extensionId = data.extensionId;
        }

        if (!data.shortName && existingSeat.shortName) {
          statusUpdatePayload.shortName = existingSeat.shortName;
          // console.log(`⚠️ Preserving existing shortName: ${existingSeat.shortName} for seat ${data.agentMapSeatId}`);
        } else {
          statusUpdatePayload.shortName = data.shortName;
        }
      } else {
        // Just pass whatever we have
        statusUpdatePayload.extensionId = data.extensionId;
        statusUpdatePayload.shortName = data.shortName;
      }

      // Handle LOGGEDOFF events - special case
      const isLoggedOff = data.nextState === 'LOGGEDOFF' || data.statusName === 'LOGGEDOFF';
      if (isLoggedOff) {
        // console.log(`🔄 LOGGEDOFF event for seat ${data.agentMapSeatId}`);
        store.dispatch(clearSeatData(data.agentMapSeatId));
        store.dispatch({ type: 'agentMap/forceChartUpdate', payload: { timestamp: Date.now() } });
        return;
      }

      // console.log(
      //   `🔄 Updating seat ${data.agentMapSeatId} status to ${statusUpdatePayload.statusName} with extensionId=${statusUpdatePayload.extensionId}`
      // );
      store.dispatch(updateSeatComprehensive(statusUpdatePayload));
      store.dispatch({ type: 'agentMap/forceChartUpdate', payload: { timestamp: Date.now() } });
    });
  }

  private setupCounterHandler(): void {
    onReceiveTotalCallandChat((data) => {
      // Detailed logging of counter data
      // console.log('🔢 COUNTER SOCKET DATA:', JSON.stringify(data, null, 2));
      // console.log('🔑 Counter data keys:', data ? Object.keys(data) : 'NULL');

      if (!data || !data.extensionId || !data.counterType) {
        // console.log('⚠️ Invalid counter data - missing required fields');
        return;
      }

      // Debounce counter updates with a unique key based on extensionId
      const eventKey = `${data.extensionId}-${data.counterType}`;
      const now = Date.now();

      // Skip event if processed in the last 500ms
      if (this.lastProcessedCounters[eventKey] && now - this.lastProcessedCounters[eventKey] < 500) {
        // console.log(`🚫 SocketService: Skipping duplicate event ${eventKey}`);
        return;
      }

      // Mark as processed
      this.lastProcessedCounters[eventKey] = now;

      // console.log('🔢 SocketService: Processing counter update:', {
      //   extensionId: data.extensionId,
      //   counterType: data.counterType
      // });

      // Find the agentMapSeatId by extensionId from the current map data
      const state = store.getState();
      const onlineData = state.agentMap.onlineDataForChart || [];
      const seat = onlineData.find((s) => s.extensionId === data.extensionId);

      if (seat?.agentMapSeatId) {
        // Dispatch with the found agentMapSeatId
        store.dispatch(
          incrementCounter({
            seatId: seat.agentMapSeatId,
            counterType: data.counterType
          })
        );
      } else {
        // console.log(`⚠️ Could not find seat with extensionId: ${data.extensionId}`);
      }
    });
  }

  // New method to handle dashboard updates
  private setupDashboardUpdatesHandler(): void {
    if (this.dashboardHandlerInitialized) {
      // console.log('🛡️ SocketService: Dashboard handler already initialized, skipping');
      return;
    }

    // console.log('🔧 SocketService: Setting up dashboard handler (first time)');
    this.dashboardHandlerInitialized = true;

    // Unregister any existing handlers first
    offReceiveGetCallDetailsEvent();

    // Register ONE handler with global deduplication
    onReceiveGetCallDetailsEvent((data: DashboardUpdateDto) => {
      // console.log('📥 EVENT RECEIVED:', JSON.stringify(data || {}).substring(0, 100) + '...');

      if (!data?.callSource || !Array.isArray(data.updates)) {
        // console.warn('⚠️ Invalid dashboard update data');
        return;
      }

      // Create a stable hash of the event
      const eventHash = objectHash(data);
      const source = data.callSource;
      const now = Date.now();
      const cacheKey = `dashboard_${source}`;

      // Check global event buffer for duplicates
      const recent = recentEvents.get(cacheKey);
      if (recent && now - recent.timestamp < GLOBAL_DEDUP_WINDOW && recent.hash === eventHash) {
        console.log(`🚫 GLOBAL BUFFER: Blocking duplicate event for ${source} (${now - recent.timestamp}ms ago)`);
        return;
      }

      // Update global buffer with this event
      recentEvents.set(cacheKey, {
        timestamp: now,
        hash: eventHash
      });

      // Process the event
      // Only log the first occurrence of an update
      const updateKey = this.getDashboardUpdateKey(data);
      const updateInfo = this.lastProcessedDashboardUpdates[updateKey];

      if (!updateInfo || updateInfo.processCount < 1) {
        console.log('📊 SocketService: Dashboard update received:', JSON.stringify(data, null, 2));
      }

      if (!data || !data.callSource || !data.updates || !Array.isArray(data.updates)) {
        console.warn('SocketService: Invalid dashboard update data received.');
        return;
      }

      // Get current state to check if we need to process this update
      const currentState = store.getState();
      const currentSourceState = currentState.agentMap.dashboard.callStats.find(
        (stat: any) => stat.callSource === data.callSource
      );

      // Create a snapshot of what the new state would be
      const pendingUpdates: Record<string, number> = {};
      let requiresUpdate = false;

      data.updates.forEach((update) => {
        const { field, action, value = 1 } = update;
        if (!field || !action) return;

        // Calculate what the new value would be
        const currentValue = currentSourceState?.[field] || 0;
        let newValue = currentValue;

        switch (action.toLowerCase()) {
          case 'increment':
            newValue = currentValue + value;
            break;
          case 'decrement':
            newValue = Math.max(0, currentValue - value);
            break;
          case 'set':
            newValue = value;
            break;
        }

        // Only mark for update if the value would actually change
        if (newValue !== currentValue) {
          pendingUpdates[field] = newValue;
          requiresUpdate = true;
        }
      });

      // If no actual changes would be made, skip processing
      if (!requiresUpdate) {
        console.log(`🛑 SocketService: Skipping dashboard update for ${data.callSource} - no changes needed`);
        return;
      }

      // Convert pending updates to a state string for deduplication check
      const pendingStateString = JSON.stringify(pendingUpdates);

      // Check if we've processed this exact update recently
      if (updateInfo) {
        updateInfo.processCount++;

        // If we've seen this exact update (same values) AND it's within our timeout window
        if (
          updateInfo.state === pendingStateString &&
          now - updateInfo.timestamp < this.DASHBOARD_EVENT_DEDUP_TIMEOUT
        ) {
          console.log(
            `🛑 Skipping duplicate dashboard update #${updateInfo.processCount} for ${data.callSource} (processed ${now - updateInfo.timestamp}ms ago)`
          );
          return;
        }

        // This is either a new update or outside our timeout window - process it
        updateInfo.timestamp = now;
        updateInfo.state = pendingStateString;
      } else {
        // First time seeing this update
        this.lastProcessedDashboardUpdates[updateKey] = {
          timestamp: now,
          state: pendingStateString,
          processCount: 1
        };
      }

      // Process each update
      const { callSource } = data;

      // Dispatch a single update with all changes
      if (Object.keys(pendingUpdates).length > 0) {
        console.log(`🔄 Dispatching dashboard update for ${callSource}:`, pendingUpdates);
        store.dispatch({
          type: 'agentMap/batchUpdateCallStats',
          payload: { callSource, updates: pendingUpdates }
        });
      }

      // Force UI refresh
      setTimeout(() => {
        store.dispatch({ type: 'agentMap/forceUIUpdate', payload: { timestamp: Date.now() } });
      }, 50);
    });
  }

  // Helper method to generate a unique key for dashboard updates
  private getDashboardUpdateKey(data: DashboardUpdateDto): string {
    if (!data || !data.callSource) return 'invalid';
    return `dashboard-${data.callSource}`;
  }
}

// Export singleton instance
export const socketService = SocketService.getInstance();
